/**
 * One new starter's case (2 Oct): what HR asked for, where each request
 * stands, and the next request to raise — already filled in from the case.
 *
 * Reading order: the reader's own next step first ("Action for you", the
 * account to confirm), then the tracker, then the buttons, then the facts from
 * the New User Request. The buttons open the ordinary request form for the
 * offering, prefilled; the person still reviews and submits it.
 *
 * Nothing can be raised before the starter is in ITSM (the gate). A candidate
 * found by name is never taken on trust: local IT confirms which person it is.
 * When the name does not match ITSM's ("Bob" vs "Robert", 5 Oct), whoever may
 * confirm picks the person from the directory instead, after a confirm dialog
 * naming both. A Closed or Cancelled request with no IT Setup is read-only.
 */

import { Check, Icon } from "@norican/icons";
import {
  Alert,
  AlertDialog,
  Button,
  DescriptionList,
  type DescriptionListEntry,
  Typography,
} from "@norican/react";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  PageContainer,
  SectionCard,
  useDateFormatter,
} from "@/components/common";
import { EmployeePicker } from "@/features/tickets";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import {
  type DirectoryEntry,
  type StarterCandidate,
  type StarterCase,
  type StarterPerson,
  useMe,
} from "@/lib/api";

import { useConfirmStarterAccount, useStarterCase } from "./api";
import { StageTracker } from "./components/stage-tracker";
import {
  availableSteps,
  startDateValue,
  starterFormPath,
  starterPageMode,
  type StarterStep,
  stepForAction,
} from "./lib/starters";

export function StarterCasePage() {
  const { caseId = "" } = useParams<{ caseId: string }>();
  const { t } = useT("tickets");
  const query = useStarterCase(caseId);
  const { data: me, isPending: mePending } = useMe();
  const item = query.data;
  useDocumentTitle(item?.new_user_name ?? t("starters.title"));

  const breadcrumbs = (
    <AppBreadcrumbs
      items={[
        { label: t("starters.title"), to: "/starters" },
        { label: item?.new_user_name ?? "…" },
      ]}
    />
  );

  if (query.isPending || mePending) {
    return (
      <PageContainer title={t("starters.title")} breadcrumbs={breadcrumbs}>
        <LoadingState />
      </PageContainer>
    );
  }
  if (query.isError || item === undefined) {
    return (
      <PageContainer title={t("starters.title")} breadcrumbs={breadcrumbs}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContainer>
    );
  }

  // 6 Oct (product owner): one page per person while the front door is on.
  const mode = starterPageMode(
    me?.access_front_door === true,
    item.access_path,
    item.actions,
    me?.access_new_forms === true,
  );
  if (mode.redirectTo !== null) {
    return <Navigate to={mode.redirectTo} replace />;
  }
  return (
    <PageContainer
      title={item.new_user_name}
      subtitle={t("starters.caseSubtitle", { number: item.nur_number, status: item.nur_status })}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex flex-col gap-6">
        {item.read_only ? <Alert tone="neutral">{t("starters.readOnly")}</Alert> : null}
        <ActionsForYou item={item} actions={mode.actions} />
        <AccountSection item={item} />
        <SectionCard title={t("starters.tracker")}>
          <StageTracker stages={item.stages ?? []} />
        </SectionCard>
        {item.read_only || !mode.showNextRequests ? null : <RaiseSection item={item} />}
        <DetailsSection item={item} />
      </div>
    </PageContainer>
  );
}

function ActionsForYou({
  item,
  actions,
}: {
  item: StarterCase;
  actions: Readonly<NonNullable<StarterCase["actions"]>>;
}) {
  const { t } = useT("tickets");
  if (actions.length === 0) {
    return null;
  }
  return (
    <Alert tone="info" title={t("starters.actionForYou")}>
      <ul className="m-0 list-disc ps-5">
        {actions.map((action) => (
          <li key={action}>{t(`starters.action.${action}`, { name: item.new_user_name })}</li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * The account gate on its own (5 Oct, product owner's UAT): the access
 * request page said "Waiting for Bob Jones's account" on every item with no
 * way to act on it. Shown there while the account is not settled.
 */
export function StarterAccountGate({ caseId }: { caseId: string }) {
  const query = useStarterCase(caseId);
  const item = query.data;
  if (item === undefined || item.read_only || item.account.state === "ready") {
    return null;
  }
  return <AccountSection item={item} />;
}

function AccountSection({ item }: { item: StarterCase }) {
  const { t } = useT("tickets");
  // 7 Oct demo: with a candidate, the search is behind a link, so it does not
  // read as the thing to do (the product owner thought it had to be used).
  const [picking, setPicking] = useState(false);
  const account = item.account;
  // 21 Oct (decisions A and B): with the automatic gate, nobody confirms;
  // the HelpDesk finds the person in ITSM itself. Admins keep the fallback.
  const auto = item.auto_gate === true;
  if (
    account.state === "waiting" ||
    (auto && account.state === "candidates" && !item.can_confirm)
  ) {
    // 28 Oct (Rusty): with the automatic gate, one line for everyone but the
    // admins, who keep the panel and the person picker.
    if (auto && !item.can_pick_person) {
      return (
        <Typography as="p" variant="body-sm">
          {t("starters.account.waitingAuto", { name: item.new_user_name })}
        </Typography>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="neutral" title={t("starters.account.waitingTitle")}>
          {auto
            ? t("starters.account.waitingAuto", { name: item.new_user_name })
            : t("starters.account.waiting", { name: item.new_user_name })}
        </Alert>
        {item.can_pick_person ? (
          <>
            {auto ? (
              <Typography as="p" variant="caption" tone="muted">
                {t("starters.account.adminFallback")}
              </Typography>
            ) : null}
            <PickPerson item={item} />
          </>
        ) : null}
      </div>
    );
  }
  if (account.state === "ready") {
    if (item.starter == null) {
      return null;
    }
    return (
      <Typography as="p" variant="body-sm" className="flex items-center gap-2">
        <Icon icon={Check} size="sm" tone="success" />
        {t("starters.account.ready", { name: personName(item.starter) })}
        <span className="text-fg-muted">
          {account.source === "it_setup"
            ? t("starters.account.fromItSetup")
            : account.source === "auto"
              ? t("starters.account.fromAuto")
              : t("starters.account.fromConfirmed")}
        </span>
      </Typography>
    );
  }
  return (
    <SectionCard
      title={t("starters.account.candidatesTitle", { name: item.new_user_name })}
      description={auto ? t("starters.account.adminFallback") : t("starters.account.candidates")}
    >
      <ul className="flex list-none flex-col gap-3 p-0">
        {(account.candidates ?? []).map((candidate) => (
          <li key={candidate.rec_id}>
            <CandidateRow item={item} candidate={candidate} />
          </li>
        ))}
      </ul>
      {item.can_confirm || item.read_only ? null : (
        <Typography as="p" variant="caption" tone="muted" className="mt-3">
          {t("starters.account.onlyOwner")}
        </Typography>
      )}
      {item.can_pick_person ? (
        picking ? (
          <div className="mt-4">
            <PickPerson item={item} />
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 self-start"
            onClick={() => setPicking(true)}
          >
            {t("starters.account.pickOther")}
          </Button>
        )
      ) : null}
    </SectionCard>
  );
}

/**
 * "Pick the person in ITSM" (5 Oct): any directory person, when the name on
 * the request does not match the account's. A dialog names both before the
 * confirmation note is written.
 */
function PickPerson({ item }: { item: StarterCase }) {
  const { t } = useT("tickets");
  const confirm = useConfirmStarterAccount(item.case_id);
  const [picked, setPicked] = useState<DirectoryEntry | null>(null);
  const [asking, setAsking] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <Typography as="p" variant="body-sm" className="font-medium">
        {t("starters.account.pick")}
      </Typography>
      <Typography as="p" variant="caption" tone="muted">
        {t("starters.account.pickHint")}
      </Typography>
      <div className="flex flex-wrap items-center gap-3">
        <EmployeePicker
          name="starter-pick-person"
          value={picked?.id ?? null}
          defaultDisplayValue={picked?.display_name ?? null}
          onChange={setPicked}
          aria-label={t("starters.account.pickLabel")}
          className="min-w-64 flex-1"
        />
        <Button size="sm" disabled={picked === null} onClick={() => setAsking(true)}>
          {t("starters.account.confirm")}
        </Button>
      </div>
      {confirm.isError ? <ErrorState variant="inline" error={confirm.error} /> : null}
      <AlertDialog.Root open={asking && picked !== null} onOpenChange={setAsking}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup className="flex flex-col gap-4">
            <AlertDialog.Title>{t("starters.account.pickTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("starters.account.pickBody", {
                person: pickedLabel(picked),
                name: item.new_user_name,
              })}
            </AlertDialog.Description>
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Close render={<Button variant="outline" tone="neutral" />}>
                {t("starters.account.pickCancel")}
              </AlertDialog.Close>
              <Button
                loading={confirm.isPending}
                disabled={confirm.isPending || picked === null}
                onClick={() => {
                  if (picked === null) {
                    return;
                  }
                  confirm.mutate(
                    { employeeId: picked.id, fromDirectory: true },
                    { onSettled: () => setAsking(false) },
                  );
                }}
              >
                {t("starters.account.pickConfirm")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}

function pickedLabel(person: DirectoryEntry | null): string {
  if (person === null) {
    return "—";
  }
  return person.email ? `${person.display_name} (${person.email})` : person.display_name;
}

function CandidateRow({ item, candidate }: { item: StarterCase; candidate: StarterCandidate }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const confirm = useConfirmStarterAccount(item.case_id);
  const created =
    candidate.created_at == null
      ? t("starters.account.createdUnknown")
      : formatDate(candidate.created_at);
  const label = t("starters.account.candidate", {
    name: candidate.display_name ?? candidate.rec_id,
    email: candidate.email ?? "—",
    date: created,
  });
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Typography as="span" variant="body-sm" className="min-w-0 flex-1">
        {label}
      </Typography>
      {item.can_confirm ? (
        <Button
          size="sm"
          loading={confirm.isPending}
          disabled={confirm.isPending}
          onClick={() => confirm.mutate({ employeeId: candidate.rec_id })}
        >
          {t("starters.account.confirm")}
        </Button>
      ) : null}
      {confirm.isError ? (
        <ErrorState variant="inline" error={confirm.error} className="basis-full" />
      ) : null}
    </div>
  );
}

const STEP_LABEL_KEY: Record<StarterStep, `starters.raise.${StarterStep}`> = {
  it_setup: "starters.raise.it_setup",
  systems: "starters.raise.systems",
  crm: "starters.raise.crm",
};

function RaiseSection({ item }: { item: StarterCase }) {
  const { t } = useT("tickets");
  const steps = availableSteps(item);
  const mine = new Set((item.actions ?? []).map(stepForAction));
  const name = item.starter?.display_name ?? item.new_user_name;
  return (
    <SectionCard title={t("starters.raise.title")} description={t("starters.raise.intro")}>
      {steps.length === 0 ? (
        <Typography as="p" variant="body-sm" tone="muted">
          {t("starters.raise.notYet", { name: item.new_user_name })}
        </Typography>
      ) : (
        <div className="flex flex-wrap gap-2">
          {steps.map((step) => (
            <Button
              key={step}
              variant={mine.has(step) ? "solid" : "outline"}
              tone={mine.has(step) ? undefined : "neutral"}
              render={<Link to={starterFormPath(item.case_id, step)} />}
            >
              {t(STEP_LABEL_KEY[step], { name })}
            </Button>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function DetailsSection({ item }: { item: StarterCase }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const start = startDateValue(item.start_date);
  const access =
    item.system_access_needed == null
      ? t("starters.details.unknown")
      : item.system_access_needed
        ? t("starters.details.yes")
        : t("starters.details.no");
  const entries: DescriptionListEntry[] = [
    {
      label: t("starters.details.startDate"),
      value:
        start === null
          ? (item.start_date_text ?? t("starters.details.unknown"))
          : formatDate(start),
    },
    { label: t("starters.details.jobTitle"), value: item.job_title ?? "—" },
    { label: t("starters.details.location"), value: item.primary_location ?? "—" },
    { label: t("starters.details.manager"), value: personName(item.manager) },
    { label: t("starters.details.referenceUser"), value: personName(item.reference_user) },
    { label: t("starters.details.systemAccess"), value: access },
    { label: t("starters.details.raisedBy"), value: personName(item.raised_by) },
    {
      label: t("starters.details.owner"),
      value:
        [personName(item.owner), item.owner_team].filter((v) => v && v !== "—").join(" · ") || "—",
    },
  ];
  return (
    <SectionCard title={t("starters.details.title")}>
      <DescriptionList items={entries} />
    </SectionCard>
  );
}

function personName(person: StarterPerson | null | undefined): string {
  if (person == null) {
    return "—";
  }
  return person.display_name ?? person.email ?? person.rec_id;
}
